
import { wceApi } from '@/api';
import { logout as logoutInternal } from '@/utils/authUtils';
import {
  defaultPageContent,
  defaultTextElementStyle,
  fontList,
} from '../const';
import { getCachedDesignEditor } from './design';
import type { PageItem } from './page';
import type { Elements, ElementTypeName, PageWidth } from './content';

type BoardConfig = {
  memberWrite: boolean;
  nonMemberWrite: boolean;
  secretWrite: boolean;
  reply: boolean;
};

type BoardListItem = {
  boardId: string;
  boardName: string;
  boardConfig: BoardConfig;
};

type Board = {
  settingUrl: string;
  enabled: boolean;
  boardList: BoardListItem[];
};

type MapInfo = {
  apiKey: string;
  LatLng: {
    latitude: number;
    longitude: number;
  };
  level: number;
};

type MapData = {
  settingUrl: string;
  enabled: boolean;
  mapInfo: MapInfo | {};
};

type Sns = {
  settingUrl: string;
  enabled: boolean;
  imageList: {
    url: string;
  }[];
};

type CategoryListItem = {
  categoryId: string;
  categoryName: string;
  parentCategoryId: string | null;
  depth: number;
  displayOrder: number;
};

type ProductCategory = {
  settingUrl: string;
  enabled: boolean;
  categoryList: CategoryListItem[];
};

type SectionListItem = {
  sectionId: string;
  sectionName: string;
};

type ProductSection = {
  settingUrl: string;
  enabled: boolean;
  sectionList: SectionListItem[];
};

type TemplateItemInfo = {
  isComing: boolean;
  templateId: string;
  defaultImage: string;
  fogImage: string;
  sampleLink: string;
  title: string;
  subTitle: string;
  layoutType?: string;
};

type FixedLayoutTypes = 'top' | 'left' | 'topleft';

type TemplateLayoutItem = {
  type?: FixedLayoutTypes;
  defaultImage: string;
  fogImage?: string;
  sampleLink: string;
  title: string;
  subTitle: string;
  detailImages: [string, string, string];
};

type TemplateInfo = {
  enabled: boolean;
  templateList: TemplateItemInfo[];
  templateLayoutTypes?: TemplateLayoutItem[];
  currentFixedLayoutType?: FixedLayoutTypes;
  showTutorial?: boolean;
};

type FixedTemplateListForAllSkin = {
  isComing: boolean;
  defaultImage: string;
  fogImage: string;
  templateId: string;
  detailImage: {
    login: string;
    product: string;
    mypage: string;
    order: string;
  };
};

type FixedTemplateInfo = {
  currentTemplateId: string;
  templateList: FixedTemplateListForAllSkin[];
};

type FixedTemplateListForProductView = {
  isComing: boolean;
  type: string;
  defaultImage: string;
  fogImage: string;
  mobileImage: string[];
  pcImage: string;
};

type ProductViewTypeInfo = {
  currentProductViewType: string;
  productViewTypeList: FixedTemplateListForProductView[];
};

type DefaultTextElementStyle = Record<string, {
  fontFamily: string;
  fontSize: number;
}>;

type PageEntities = {
  [pageId: string]: PageItem;
};

type PageState = {
  linked: string[];
  fixedUnlinked?: string[];
  unlinked: string[];
  entities: PageEntities;
  fontFamily: string | null;
};

type ElementToolMenuName = 'headerCategory' | 'headerMenus' | 'headerIcons';

type SceneState = {
  pageId: string;
  sectionIds: string[];
  selectedElementIds: string[];
  selectCandidateElementIds: string[];
  autoOpenElementTool: {
    elementType?: ElementTypeName;
    selectedMenu?: ElementToolMenuName;
  }[];
  background: {type: string; value: string};
  pageWidth: PageWidth;
  elements: Elements;
};

type Language = 'ko' | 'ja' | 'en' | 'de';

type TopMenuOption = {
  tutorialGuide: boolean;
  videoGuide: boolean;
};

type HeaderPageOption = {
  showOnlySelectedSection: boolean;
};

type WceHeaderDefaultElementOption = {
  enable?: boolean;
  enableVerticalType?: boolean;
  enableComplexTypeSectionMove?: boolean;
};

type HeaderElementOption = {
  headerCategory: WceHeaderDefaultElementOption;
  headerMenus: WceHeaderDefaultElementOption;
  headerIcons: WceHeaderDefaultElementOption;
};

type MobileHeaderElementOption = {
  headerMobileMenu: boolean;
  headerMobileSearch: boolean;
  headerMobileCart: boolean;
};

type CommonElementOption = {
  image?: boolean;
  slider?: boolean;
  text?: boolean;
  button?: boolean;
  video?: boolean;
  map?: boolean;
  sns?: {
    instagram: boolean;
  };
  board?: boolean;
  product?: boolean;
  productSlide?: boolean;
};

type FooterPolicyItem = 'term' | 'policy' | 'specifiedCommercialLaw';

type WceFooterPolicyElementState = {
  enable: boolean;
  policies: FooterPolicyItem[];
};

type WceFooterShopinfoElementState = {
  enable: boolean;
  showOnlyCopyright: boolean;
};

type FooterElementOption = {
  footerPolicy: WceFooterPolicyElementState;
  footerShopinfo: WceFooterShopinfoElementState;
};

type WceOptionsState = {
  language: Language;
  useLayoutType: boolean;
  useOnlyProductStyleFixedTemplate: boolean;
  useFixedTemplate: boolean;
  useSeperatePageBackgroundForHeaderFooter: boolean;
  topMenu: TopMenuOption;
  headerPage: HeaderPageOption;
  headerElement: HeaderElementOption;
  mobileHeaderElement: MobileHeaderElementOption;
  commonElement: CommonElementOption;
  footerElement: FooterElementOption;
};

type GetDesignShopsResponse = {
  boards: {
    boardDescription: string | null;
    boardName: string | null;
    boardNo: number | null;
    guestWrite: boolean | null;
    memberWrite: boolean | null;
    reply: boolean | null;
    secret: boolean | null;
  }[] | null;
  categories: {
    categoryId: string | null;
    categoryName: string | null;
    depth: number | null;
    displayOrder: number | null;
    parentCategoryId: string | null;
  }[] | null;
  currentTemplateId: string | null;
  fixedTemplates: {
    coming: boolean | null;
    defaultImage: string | null;
    detailImage: {
      login: string | null;
      mypage: string | null;
      order: string | null;
      product: string | null;
    } | null;
    fogImage: string | null;
    templateId: string | null;
  }[] | null;
  instagramImagesJson?: {
    data: {
      media_type: string | null;
      media_url: string | null;
      permalink: string | null;
      thumbnail_url: string | null;
    }[] | null;
    instagramResponse: {
      error: {
        code: number | null;
        fbtrace_id: string | null;
        message: string | null;
        type: string | null;
      };
      permissionError: boolean | null;
      temporaryError: boolean;
      tokenError: boolean | null;
    } | null;
  } | null;
  menus: {
    linked: string[] | null;
    unlinked: string[] | null;
  } | null;
  pageInfos: {
    content: string | null;
    pageId: string | null;
    pageInfo: string | null;
    title: string | null;
    url: string | null;
  }[] | null;
  readyshopConfig: {
    agreeCPA: string | null;
    agreeCPAYmdt: string | null;
    appkeys: {
      android?: string;
      NAVER?: string;
    } | null;
    appSecrets: {
      android?: string;
      NAVER?: string;
    } | null;
    bankdaExpirationYmdt: string | null;
    companyInfoLinkAddress: string | null;
    enabledBankda: boolean | null;
    enableOtp: boolean | null;
    enableSale: boolean | null;
    favicon: string | null;
    fontFamily: string | null;
    googleAnalytics: string | null;
    instagramAccessToken: string | null;
    instagramExpiredYmdt: string | null;
    kakaoMapKey: string | null;
    kakaoMapLatitude: string | null;
    kakaoMapLongitude: string | null;
    logoId: string | null;
    logoText: string | null;
    logoType: string | null;
    logoUrl: string | null;
    mallDescription: string | null;
    mallDns: string | null;
    managerBirthDay: string | null;
    managerName: string | null;
    mobileLogoId: string | null;
    mobileLogoUrl: string | null;
    naverCommonAuthKey: string | null;
    naverMallGradeType: string | null;
    naverShoppingUseYn: string | null;
    naverWebmaster: string | null;
    notifyInstagramExpired: boolean | null;
    objectStoragePath: string | null;
    otpSecret: string | null;
    paycoCallbackDomain: string | null;
    representativeImageHeight: number | null;
    representativeImageUrl: string | null;
    representativeImageWidth: number | null;
    sealImageUrl: string | null;
    serviceCode: string | null;
    showDefaultTemplateSelector: boolean | null;
    showEasyTemplateSelector: boolean | null;
    showTutorial: boolean | null;
    templateType: string | null;
    useScript: boolean | null;
  } | null;
  sections: {
    sectionId: string | null;
    sectionName: string | null;
  }[] | null;
  templateInfos: {
    coming: boolean | null;
    defaultImage: string | null;
    fogImage: string | null;
    sampleLink: string | null;
    subtitle: string | null;
    templateId: string | null;
    templateOrder: number | null;
    title: string | null;
  }[] | null;
};

export const initialize = (
  cb: (result: {
    initPage?: string;
    links: Record<string, string>;
    configResource: Record<string, string>;
    sns: Sns;
    board: Board;
    map: MapData;
    productCategory: ProductCategory;
    productSection: ProductSection;
    templateInfo: TemplateInfo;
    fixedTemplateInfo?: FixedTemplateInfo;
    productViewTypeInfo?: ProductViewTypeInfo;
    defaultTextElementStyle: DefaultTextElementStyle;
    page?: PageState;
    scene?: SceneState;
    options?: WceOptionsState;
  }) => void,
) => {
  getCachedDesignEditor(({ readyshopConfig }) => {
    wceApi.post<void>('/design/pages/initialize')
      .then(() => {
        wceApi.get<GetDesignShopsResponse>('/design/shops')
          .then(({
            data: {
              boards,
              categories,
              currentTemplateId,
              fixedTemplates,
              instagramImagesJson,
              menus,
              pageInfos,
              sections,
              templateInfos,
            },
          }) => {
            const entities = pageInfos?.reduce((acc, { content, pageId, title, url }) => {
              const parsedContent = content && JSON.parse(content) || defaultPageContent();
              acc[pageId] = {
                title,
                url,
                ...parsedContent,
              };

              return acc;
            }, {} as PageEntities);

            const boardList = boards?.map(({
              boardName,
              boardNo,
              guestWrite,
              memberWrite,
              reply,
              secret,
            }) => {
              return {
                boardId: boardNo.toString(),
                boardName,
                boardConfig: {
                  memberWrite,
                  nonMemberWrite: guestWrite,
                  secretWrite: secret,
                  reply,
                },
              };
            }) ?? [];

            const snsImages = instagramImagesJson?.data?.map(
              (data) => data?.media_type == 'VIDEO' ? {
                url: data.thumbnail_url,
              } : {
                url: data.media_url,
              }
            );

            const templateInfo = {
              enabled: readyshopConfig?.showDefaultTemplateSelector,
              showTutorial: readyshopConfig?.showTutorial,
              templateList: templateInfos?.map(({
                coming,
                defaultImage,
                fogImage,
                sampleLink,
                subtitle,
                templateId,
                title,
              }) => ({
                isComing: coming,
                templateId,
                defaultImage,
                fogImage,
                sampleLink,
                title,
                subTitle: subtitle,
              })),
            };

            const fixedTemplateInfo = {
              currentTemplateId,
              templateList: fixedTemplates?.map(({
                coming,
                defaultImage,
                detailImage,
                fogImage,
                templateId,
              }) => {
                const template: any = {
                  isComing: coming,
                  templateId,
                  defaultImage,
                };

                if (!coming) {
                  template.detailImage = detailImage;
                  template.fogImage = fogImage;
                }

                return template;
              }) ?? [],
            };

            const imgBasePath = 'http://image.toast.com/aaaaahb/readyshop/design-editor';

            const configResource = {
              mallLogo: readyshopConfig?.mobileLogoUrl || '/style/img/common/logo.png',
              sprDesigntool: `${imgBasePath}/spr-designtool.png`,
              bgPreviewLoading: `${imgBasePath}/bg-preview-loading.png`,
              txtRedCheck: `${imgBasePath}/txt-red-check.png`,
              txtPageWidth: `${imgBasePath}/txt-page-width.png`,
              txtPageFixed: `${imgBasePath}/txt-page-fixed.png`,
              txtOverrapMode: `${imgBasePath}/txt-overrap-mode.png`,
              txtMobileSectionOn: `${imgBasePath}/txt-mobile-section-on.png`,
              txtMobileSeciontOff: `${imgBasePath}/txt-mobile-section-off.png`,
              txtMobileSec: `${imgBasePath}/txt-mobile-sec1.png`,
              txtGrayCheck: `${imgBasePath}/txt-gray-check.png`,
            };

            const params = {
              page: {
                entities,
                linked: menus?.linked,
                unlinked: menus?.unlinked,
                fontFamily: readyshopConfig?.fontFamily || fontList[0].fontFamily,
              },
              links: {
                admin: '/',
                tutorial: 'https://gongji.godo.co.kr/userinterface/shopby/panel/tutorial/index.html'
              },
              sns: {
                settingUrl: '/external',
                enabled: true,
                imageList: snsImages
              },
              map: {
                settingUrl: '/external',
                enabled: true,
                mapInfo: {
                  apiKey: readyshopConfig?.kakaoMapKey,
                  LatLng: {
                    latitude: readyshopConfig?.kakaoMapLatitude,
                    longitude: readyshopConfig?.kakaoMapLongitude,
                  },
                  level: 3,
                },
              },
              board: {
                settingUrl: '/board',
                enabled: true,
                boardList,
              },
              productCategory: {
                settingUrl: '/display/category',
                enabled: true,
                categoryList: categories,
              },
              productSection: {
                settingUrl: '/appearance/custom/product-main',
                enabled: true,
                sectionList: sections,
              },
              templateInfo,
              fixedTemplateInfo,
              configResource,
              defaultTextElementStyle,
            };

            cb(params);
          });
      });
  });
};

export const logout = () => {
  logoutInternal();
};
